import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Box,
    Card,
    CircularProgress,
    Fade,
    ListItemIcon,
    MenuItem,
    Paper,
    Skeleton,
    Stack,
    SvgIcon,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from "@mui/material";
import {formatPhoneNumber} from "../../../../../../../core/services/utils/utils";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Button from "../../../../../../components/base/button/button";
import CreateUserOnlineProfileDialog from "../../../../../../components/dialogs/create-user-online-profile";
import AssignUserToCustomerDialog from "../../../../../../components/dialogs/assign-user-to-customer";
import {Switch} from "../../../../../../components/base/switch";
import Api from "../../../../../../../core/services/api/api";
import NotificationsIcon from "@mui/icons-material/Notifications";
import IconButton from "../../../../../../components/base/button/icon_button";
import UpdateUserNotificationSubscriptionsModal
    from "../../../../../../components/dialogs/update-user-notification-subscriptions";
import {
    UserGeneralInfoCompanyMembersDataGridCellKeys
} from "../../../../../../../core/constants/table/data-grid-column-keys";
import {
    dataGridCellTextProps,
    DataGridColumnAlignments,
    DataGridColumnPinnedTypes,
    DataGridColumnTypes,
    useDataGridSavedState
} from "../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import {ReactComponent as NoUsersSvg} from "../../../../../../../assets/images/art-like/no-users.svg";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {
    CheckCircleOutline,
    DeleteOutline,
    EditOutlined,
    ForwardToInbox,
    LinkOff,
    MoreHorizRounded,
    PersonAddAlt1,
    PersonOff,
    SortRounded,
    ViewCompactRounded,
} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import UpdateCustomerMemberInformationDialog
    from "../../../../../../components/dialogs/update-customer-member-information";
import {useDispatch} from "react-redux";
import {setReduxDialogState} from "../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../core/models/static/routes/redux-store-routes";
import useSavedComponentsState from "../../../../../../hooks/use-saved-components-state";
import LoadingLottieAnimation from "../../../../../../components/app-specific/lottie-animations/loading";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Popover} from "../../../../../../components/app-specific/popover";
import {usePartnerInformation} from "../../../../../../hooks/use-partner-information";
import {CustomerMemberAccessLevels, CustomerMemberShipNames} from "../../../../../../../core/constants/enums";
import ResendActivationDialog from "../../../../operator/customers/members/sections/resend-activation-dialog";
import MembershipStatusChip from "../../../../operator/customers/members/sections/membership-status-chip";
import {resolveMembershipTitle} from "../../../../operator/customers/members/sections/grid-shared";

/**
 * Status actions that match the customers members list, for a profile member row.
 * Returns nothing when membershipName and memberShipName are both missing so a missing field cannot show Delete.
 * Assign is only On Hold or Without a Customer. Rows on this page already belong to the profile customer.
 * @param {any} member
 * @param {object} options
 * @param {boolean} options.hasB2cWebsite
 * @param {boolean} options.canCreateMember
 * @param {(key: string, options?: object) => string} options.t
 * @param {(member: any) => void} options.onAssign
 * @param {(member: any) => void} options.onResendActivation
 * @param {(member: any) => void} options.onActivate
 * @param {(member: any) => void} options.onDeactivate
 * @param {(member: any) => void} options.onDelete
 * @returns {{leading: object[], trailing: object[]}}
 */
const buildProfileMemberStatusActions = (member, {
    hasB2cWebsite,
    canCreateMember,
    t,
    onAssign,
    onResendActivation,
    onActivate,
    onDeactivate,
    onDelete,
}) => {
    const status = member?.membershipName ?? member?.memberShipName;
    if (!status) {
        return {leading: [], trailing: []};
    }
    const isActive = status === CustomerMemberShipNames.active;
    const isDeactivated = status === CustomerMemberShipNames.deactivated;
    const isOnHold = status === CustomerMemberShipNames.onHold;
    const isOnHoldCompanyLost = status === CustomerMemberShipNames.onHoldCompanyLost;
    const leading = [];
    const trailing = [];
    if (canCreateMember && (isOnHold || isOnHoldCompanyLost)) {
        leading.push({
            id: 'assign',
            label: t('rowActions.assign'),
            Icon: PersonAddAlt1,
            onClick: () => onAssign(member),
        });
    }
    if (hasB2cWebsite && isOnHold) {
        leading.push({
            id: 'resendActivation',
            label: t('rowActions.resendActivation'),
            Icon: ForwardToInbox,
            onClick: () => onResendActivation(member),
        });
    }
    if (isDeactivated) {
        leading.push({
            id: 'activate',
            label: t('rowActions.activate'),
            Icon: CheckCircleOutline,
            onClick: () => onActivate(member),
        });
    }
    if (isActive) {
        trailing.push({
            id: 'deactivate',
            label: t('rowActions.deactivate'),
            Icon: PersonOff,
            onClick: () => onDeactivate(member),
        });
    }
    if (!isActive) {
        trailing.push({
            id: 'delete',
            label: t('rowActions.delete'),
            Icon: DeleteOutline,
            destructive: true,
            onClick: () => onDelete(member),
        });
    }
    return {leading, trailing};
};

/**
 * Renders one status action as a menu item.
 * @param {object} props
 * @param {object} props.item
 * @param {boolean} props.disabled
 * @param {() => void} [props.close]
 */
const ProfileMemberStatusAction = ({item, disabled, close}) => {
    const Icon = item.Icon;

    /**
     * Runs the action and closes the menu.
     * @type {function(): void}
     */
    const run = useCallback(() => {
        item.onClick();
        close?.();
    }, [close, item]);

    return (
        <MenuItem
            disabled={disabled}
            sx={{margin: 0, width: '100%', ...(item.destructive ? {color: 'error.main'} : {})}}
            onClick={run}
        >
            <ListItemIcon sx={{
                minWidth: 36,
                color: item.destructive ? 'error.main' : 'inherit',
            }}>
                <Icon fontSize="small"/>
            </ListItemIcon>
            {item.label}
        </MenuItem>
    );
};

/**
 * Three-dot menu of member actions, shared by the table and the cards.
 * @param {object} props
 * @param {any} props.member
 * @param {boolean} props.deleting
 * @param {boolean} [props.triggerDisabled]
 * @param {boolean} props.showNotifications
 * @param {(member: any) => void} props.openMemberSubscriptions
 * @param {(member: any) => void} props.openEditMemberInformationDialog
 * @param {(member: any) => void} props.removeMember
 * @param {object} props.statusActionOptions
 */
/**
 * Colored membership status for a profile member. Reads membershipName, then memberShipName.
 * @param {object} props
 * @param {any} props.member
 */
const ProfileMemberStatusChip = ({member}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.customers.members'});
    const status = member?.membershipName ?? member?.memberShipName;
    if (!status) {
        return null;
    }
    return (
        <MembershipStatusChip
            status={status}
            label={resolveMembershipTitle(status, t)}
        />
    );
};

const ProfileMemberActionsMenu = ({
                                      member,
                                      deleting,
                                      triggerDisabled = false,
                                      showNotifications,
                                      openMemberSubscriptions,
                                      openEditMemberInformationDialog,
                                      removeMember,
                                      statusActionOptions,
                                      dataGrid = false,
                                  }) => {
    const statusActions = buildProfileMemberStatusActions(member, statusActionOptions);
    const itemDisabled = deleting || member.removing;

    return (
        <Popover
            elevation={2}
            transformOrigin={dataGrid ? {vertical: 'top', horizontal: 'left'} : {vertical: 'top', horizontal: 'right'}}
            child={(onEnter) =>
                <IconButton
                    disabled={triggerDisabled}
                    className={"mx-1 ml-2"}
                    color={'paper.secondary'}
                    onPressed={onEnter}
                >
                    <SvgIcon className={"icon normal"} component={MoreHorizRounded}/>
                </IconButton>
            }
            popoverChild={(_, close) => (
                <Box sx={{gap: 0, display: 'flex', flexDirection: 'column', padding: 1}}>
                    {
                        showNotifications &&
                        <MenuItem
                            disabled={itemDisabled}
                            sx={{margin: 0, width: '100%'}}
                            onClick={() => {
                                openMemberSubscriptions(member);
                                close();
                            }}
                        >
                            <div className={'subscription-bell'}>
                                <Fade
                                    in={member.activeSubscriptionsForCurrentOperator?.length > 0}
                                    className={'count-icon'}>
                                    <div className={''}>
                                        <span {...dataGridCellTextProps}>
                                            {member.activeSubscriptionsForCurrentOperator?.length}
                                        </span>
                                    </div>
                                </Fade>
                                <NotificationsIcon className={'icon normal'}/>
                            </div>
                            <span className='mx-2'>
                                Notification Subscriptions
                            </span>
                        </MenuItem>
                    }
                    <MenuItem
                        disabled={itemDisabled}
                        sx={{margin: 0, width: '100%'}}
                        onClick={() => {
                            openEditMemberInformationDialog(member);
                            close();
                        }}
                    >
                        <ListItemIcon sx={{minWidth: 36, color: 'inherit'}}>
                            <EditOutlined fontSize="small"/>
                        </ListItemIcon>
                        Edit Information
                    </MenuItem>
                    {
                        statusActions.leading.map((item) => (
                            <ProfileMemberStatusAction
                                key={item.id}
                                item={item}
                                disabled={itemDisabled}
                                close={close}
                            />
                        ))
                    }
                    <MenuItem
                        disabled={itemDisabled}
                        sx={{margin: 0, width: '100%'}}
                        onClick={() => {
                            removeMember(member);
                            close();
                        }}
                    >
                        <ListItemIcon sx={{minWidth: 36, color: 'inherit'}}>
                            {
                                member.removing
                                    ? <CircularProgress size={18}/>
                                    : <LinkOff fontSize="small"/>
                            }
                        </ListItemIcon>
                        Detach Member from Customer
                    </MenuItem>
                    {
                        statusActions.trailing.map((item) => (
                            <ProfileMemberStatusAction
                                key={item.id}
                                item={item}
                                disabled={itemDisabled}
                                close={close}
                            />
                        ))
                    }
                </Box>
            )}
        />
    );
};

const CustomerMembersSection = ({customer, formData, deleting}) => {
    const [members, setMembers] = useState([]);
    const [_accessLevels, setAccessLevels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openAddUser, setOpenAddUser] = useState(false);
    const [subscriptions, setSubscriptions] = useState({open: false, member: null})
    const [editMemberInformationDialogOpen, setEditMemberInformationDialogOpen] = useState({open: false, member: null});
    const [hasB2cWebsite, setHasB2cWebsite] = useState(false);
    const [assignDialog, setAssignDialog] = useState({open: false, member: null});
    const [resendDialog, setResendDialog] = useState({open: false, member: null, members: null});
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const dispatch = useDispatch();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.customers.members'});
    const canCreateMember = operatorAccessLevels.packages.base.createCustomerMember;

    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [loadingViewMode, setLoadingViewMode] = useState(true);
    const {
        highestVersion: getCachedViewMode,
        save: saveCachedViewMode
    } = useSavedComponentsState({name: "customer-members-list-view-mode", version: '1.0.0'});

    const accessLevels = useMemo(() => _accessLevels.filter(e => e.token.toUpperCase() !== CustomerMemberAccessLevels.payment), [_accessLevels])

    /**
     * Fetches the cached view mode.
     * @type {(function(): Promise<void>)}
     */
    const getViewMode = useCallback(async () => {
        setLoadingViewMode(true);
        const cachedMode = await getCachedViewMode();
        if (cachedMode?.data) {
            setViewMode(cachedMode.data);
        }
        setLoadingViewMode(false);
    }, [getCachedViewMode]);

    /**
     * Loads the cached view mode on component mount.
     */
    useEffect(() => {
        void getViewMode();
    }, [getCachedViewMode, getViewMode]);

    /**
     * Toggles the view mode between grid and list.
     * @type {(function(): void)|*}
     */
    const toggleViewMode = useCallback(() => {
        setViewMode(prevMode => {
            const newMode = prevMode === 'grid' ? 'list' : 'grid';
            void saveCachedViewMode(newMode);
            return newMode;
        });
    }, [saveCachedViewMode]);

    const newMemberDialogInitialValues = useMemo(() => ({company: customer}), [customer]);

    const partnerInformation = usePartnerInformation();
    const partner = useMemo(() => !partnerInformation ? partnerInformation : ({
        ...partnerInformation,
        logo: (partnerInformation?.configuration?.PartnerLogoUrlBaseAddress ?? '') + (partnerInformation?.logo ?? ''),
    }), [partnerInformation])

    /**
     * Fetches the access levels from the server and if successful, fetches the members of the company and then sets
     * the access levels and members.
     *
     * @type {(function(): Promise<void>)}
     */
    const getAccessLevelsAndMembers = useCallback(async () => {
        setLoading(true);
        const res = await Api.getCustomerMembersWithAccessLevels(customer?.id);
        if (!isMounted())
            return;
        if (res?.resultFlag) {
            setMembers(res.data?.map(e => ({
                ...e,
                accessLevels: operatorAccessLevels.packages.base.manageCustomerMemberAccessLevels ? (e.accessLevels?.map(a => ({
                    ...a,
                    loading: false
                })) ?? []) : [],
                loadingAdmin: false,
                removing: false,
            })) ?? []);
            if (operatorAccessLevels.packages.base.manageCustomerMemberAccessLevels) {
                const response = await Api.getMembersAccessLevels();
                if (!isMounted())
                    return;
                if (response?.resultFlag) {
                    setAccessLevels(response?.data);
                }
            }
        }
        setLoading(false);
    }, [customer?.id, isMounted, operatorAccessLevels])

    /**
     * Fetches the members for a company as soon as the component mounts.
     */
    useEffect(() => {
        getAccessLevelsAndMembers().then();
    }, [getAccessLevelsAndMembers])

    /**
     * Loads whether this partner has a B2C retail user. Resend activation stays hidden when it does not.
     * @type {function(): Promise<void>}
     */
    const loadB2cWebsiteFlag = useCallback(async () => {
        const response = await Api.getPartnerRetailUser({showErrorToast: false});
        if (!isMounted()) {
            return;
        }
        setHasB2cWebsite(!!(response?.resultFlag && response.data?.userId));
    }, [isMounted]);

    /**
     * Fetches the B2C retail-user flag once when the members section mounts.
     */
    useEffect(() => {
        void loadB2cWebsiteFlag();
    }, [loadB2cWebsiteFlag]);


    /**
     * Sets the specific access level's loading state to the value for the specific member.
     * @type {(function(*=, *=, *=): void)}
     */
    const setAccessLevelLoading = useCallback((memberId, accessLevelId, value) => {
        setMembers(prevState => prevState.map(m => m.id === memberId ? {
            ...m,
            accessLevels: m.accessLevels.map(a => a.id === accessLevelId ? {...a, loading: value} : a)
        } : m));
    }, [])

    /**
     * Sets the admin loading state to the value for the specific member.
     * @type {(function(*=, *=): void)}
     */
    const setAdminLoading = useCallback((memberId, value) => {
        setMembers(prevState => prevState.map(m => m.id === memberId ? {
            ...m,
            loadingAdmin: value,
        } : m));
    }, [])

    /**
     * Sets the remove loading state to the value for the specific member.
     * @type {(function(*=, *=): void)}
     */
    const setRemoveLoading = useCallback((memberId, value) => {
        setMembers(prevState => prevState.map(m => m.id === memberId ? {
            ...m,
            removing: value,
        } : m));
    }, [])

    /**
     * Adds the new member to the beginning of the member list.
     * @type {(function(*): void)}
     */
    const onAddSuccess = useCallback(() => getAccessLevelsAndMembers(), [getAccessLevelsAndMembers])

    /**
     * Changes the access level of a specific member by setting the state of that access level to loading, then
     * calling the api and if the api result is successful, removing the loading state.
     * @type {(function(*=, *=, *=): void)}
     */
    const changeMemberAccessLevel = useCallback(async (member, accessLevel, checked) => {
        if (accessLevel) {
            setAccessLevelLoading(member.id, accessLevel.id, true);
        } else {
            setAdminLoading(member.id, true);
        }
        const paymentAccessLevel = _accessLevels.find(e => e.token.toUpperCase() === CustomerMemberAccessLevels.payment);
        const isPurchaseAccessLevel = accessLevel?.token.toUpperCase() === CustomerMemberAccessLevels.purchase;
        const forApi = {
            id: member.id,
            hasFullAccessLevel: !accessLevel ? checked : member?.hasFullAccessLevel,
            accessLevels: !accessLevel
                ? member.accessLevels
                : checked
                    ? [...member.accessLevels, ...(isPurchaseAccessLevel ? [accessLevel, paymentAccessLevel] : [accessLevel])]
                    // remove the access level, then if the removal is purchase, also remove the payment access level
                    : member.accessLevels.filter(e => e.id !== accessLevel.id && (!isPurchaseAccessLevel || e.id !== paymentAccessLevel?.id)),
        }
        const response = await Api.updateCompanyMemberAccessLevels(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setMembers(prevState => prevState.map(m => m.id === member.id
                ? {
                    ...m,
                    accessLevels: forApi.accessLevels,
                    hasFullAccessLevel: forApi.hasFullAccessLevel,
                }
                : m))
        }
        if (accessLevel) {
            setAccessLevelLoading(member.id, accessLevel.id, false);
        } else {
            setAdminLoading(member.id, false);
        }
    }, [_accessLevels, isMounted, setAccessLevelLoading, setAdminLoading])

    /**
     * Removes a member from the company by calling the server api and if the result is successful, then removes it
     * from the ui.
     * @type {(function(*): void)}
     */
    const removeMember = useCallback(async (member) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            title: `Detach this member from their assigned customer? Member: "${member.firstName} ${member.lastName}"`,
            onClose: async (confirmed) => {
                if (!confirmed)
                    return true;
                setRemoveLoading(member.id, true);
                const response = await Api.removeCompanyFromUser(member.id);
                if (!isMounted())
                    return true;
                if (response?.resultFlag) {
                    setMembers(prevState => prevState.filter(e => e.id !== member.id));
                }
                setRemoveLoading(member.id, false);
                return response?.resultFlag;
            }
        }));
    }, [isMounted, setRemoveLoading, dispatch])

    /**
     * Opens the members subscription dialog for the selected member.
     * @type {(function(*): void)}
     */
    const openMemberSubscriptions = useCallback((member) => {
        setSubscriptions({open: true, member: member})
    }, [])

    /**
     * Closes the edit member information dialog.
     * @type {(function(): void)}
     */
    const closeMemberSubscriptions = useCallback(() => {
        setSubscriptions({open: false, member: null})
    }, [])

    /**
     * Opens the edit member information dialog for the selected member.
     * @type {(function(*): void)}
     */
    const openEditMemberInformationDialog = useCallback((member) => {
        setEditMemberInformationDialogOpen({open: true, member: member});
    }, [])

    /**
     * Closes the edit member information dialog.
     * @type {(function(): void)}
     */
    const closeEditMemberInformationDialog = useCallback(() => {
        setEditMemberInformationDialogOpen({open: false, member: null});
    }, [])

    /**
     * Given the list of new subscriptions for the member, updates the internal state list of subscription for that
     * member,
     * @type {(function(*, *): void)}
     */
    const updateMemberSubscriptions = useCallback((memberId, subscriptions) => {
        setMembers(prevState => prevState.map(e => e.id === memberId ? {
            ...e,
            activeSubscriptionsForCurrentOperator: subscriptions?.map(e => ({
                ...({
                    ...e,
                    loading: undefined,
                    selected: undefined
                })
            })),
        } : e))
    }, [])

    /**
     * Updates the member information in the internal state list of members.
     * @type {(function(*): void)}
     */
    const onMemberInfoUpdated = useCallback((member) => {
        setMembers(prevState => prevState.map(e => e.id === member.id ? {...e, ...member} : e))
    }, [])

    /**
     * Opens the assign-to-customer dialog for an On Hold or Without a Customer member.
     * @type {(function(any): void)}
     */
    const onAssign = useCallback((member) => {
        setAssignDialog({open: true, member});
    }, []);

    /**
     * Opens the resend-activation dialog for an On Hold member.
     * @type {(function(any): void)}
     */
    const onResendActivation = useCallback((member) => {
        setResendDialog({open: true, member, members: null});
    }, []);

    /**
     * Closes the resend-activation dialog.
     * @type {function(): void}
     */
    const closeResendDialog = useCallback(() => {
        setResendDialog((prev) => ({...prev, open: false}));
    }, []);

    /**
     * Confirms and deactivates an active member, then reloads the list.
     * @type {(function(any): void)}
     */
    const onDeactivate = useCallback((member) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            title: t('confirm.deactivateTitle'),
            onClose: async (confirmed) => {
                if (!confirmed) {
                    return true;
                }
                const response = await Api.toggleUserStatus(member.id);
                if (!isMounted()) {
                    return true;
                }
                if (response?.resultFlag) {
                    await getAccessLevelsAndMembers();
                }
                return response?.resultFlag;
            },
        }));
    }, [dispatch, getAccessLevelsAndMembers, isMounted, t]);

    /**
     * Activates a deactivated member and reloads the list.
     * @type {(function(any): Promise<void>)}
     */
    const onActivate = useCallback(async (member) => {
        const response = await Api.toggleUserStatus(member.id);
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag) {
            await getAccessLevelsAndMembers();
        }
    }, [getAccessLevelsAndMembers, isMounted]);

    /**
     * Confirms and permanently deletes a member, then removes the row.
     * @type {(function(any): void)}
     */
    const onDelete = useCallback((member) => {
        const memberName = `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim();
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            title: t('confirm.deleteTitle', {name: memberName}),
            onClose: async (confirmed) => {
                if (!confirmed) {
                    return true;
                }
                const response = await Api.removeCustomerMembers({userIds: [member.id]});
                if (!isMounted()) {
                    return true;
                }
                if (response?.resultFlag) {
                    setMembers((prevState) => prevState.filter((entry) => entry.id !== member.id));
                }
                return response?.resultFlag;
            },
        }));
    }, [dispatch, isMounted, t]);

    const statusActionOptions = useMemo(() => ({
        hasB2cWebsite,
        canCreateMember,
        t,
        onAssign,
        onResendActivation,
        onActivate,
        onDeactivate,
        onDelete,
    }), [
        canCreateMember,
        hasB2cWebsite,
        onActivate,
        onAssign,
        onDeactivate,
        onDelete,
        onResendActivation,
        t,
    ]);

    return (
        <>
            <Paper sx={{
                padding: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                position: 'relative',
                height: '100%'
            }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    marginBlockEnd: 2
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <Typography variant={'subtitle1'} fontWeight={600}>
                            Members
                        </Typography>
                        <Stack sx={{
                            border: "1px solid transparent",
                            borderColor: 'divider',
                            padding: 0.5,
                            borderRadius: 1,
                            width: 'fit-content',
                        }}>
                            <ToggleButtonGroup
                                disabled={loadingViewMode}
                                sx={{
                                    '& .MuiToggleButtonGroup-grouped': {
                                        borderRadius: "0.5rem",
                                        width: 30,
                                        height: 30,
                                    },
                                    "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
                                        marginLeft: ".25rem",
                                    },
                                }}
                                size={'small'}
                                value={viewMode}
                                onChange={(_, v) => toggleViewMode(v)}
                                exclusive
                            >
                                <ToggleButton
                                    value={"list"}
                                    sx={{
                                        backgroundColor: viewMode === 'list' ? "divider" : "transparent",
                                        border: "none"
                                    }}>
                                    <Tooltip title={'Show in a Table'}>
                                        <SortRounded sx={{padding: 0.25}}/>
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton
                                    value={"grid"}
                                    sx={{
                                        backgroundColor: viewMode === 'grid' ? "divider" : "transparent",
                                        border: "none"
                                    }}>
                                    <Tooltip title={"Show as cards"}>
                                        <ViewCompactRounded sx={{padding: 0.25}}/>
                                    </Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                    </Box>
                    {
                        operatorAccessLevels.packages.base.createCustomerMember &&
                        <Button
                            disabled={!!loading || deleting}
                            variant={'outlined'}
                            color={'primary'}
                            onClick={() => setOpenAddUser(true)}
                        >
                            Add Member
                        </Button>
                    }
                </Box>
                {
                    loadingViewMode &&
                    <LoadingLottieAnimation
                        width={'80%'}
                        className={'my-5'}
                    />
                }
                {
                    !loadingViewMode &&
                    viewMode === 'list' &&
                    <CustomerMembersSectionDataGrid
                        members={members}
                        accessLevels={accessLevels}
                        loading={loading}
                        removeMember={removeMember}
                        openMemberSubscriptions={openMemberSubscriptions}
                        openEditMemberInformationDialog={openEditMemberInformationDialog}
                        changeMemberAccessLevel={changeMemberAccessLevel}
                        deleting={deleting}
                        partner={partner}
                        statusActionOptions={statusActionOptions}
                    />
                }
                {
                    !loadingViewMode &&
                    viewMode === 'grid' &&
                    <CustomerMembersSectionCards
                        members={members}
                        accessLevels={accessLevels}
                        loading={loading}
                        removeMember={removeMember}
                        openMemberSubscriptions={openMemberSubscriptions}
                        openEditMemberInformationDialog={openEditMemberInformationDialog}
                        changeMemberAccessLevel={changeMemberAccessLevel}
                        deleting={deleting}
                        partner={partner}
                        statusActionOptions={statusActionOptions}
                    />
                }
            </Paper>
            <CreateUserOnlineProfileDialog
                open={openAddUser}
                setOpen={setOpenAddUser}
                initialValues={newMemberDialogInitialValues}
                onSuccess={onAddSuccess}
            />
            <UpdateUserNotificationSubscriptionsModal
                open={subscriptions.open}
                member={subscriptions.member}
                setOpen={closeMemberSubscriptions}
                onChange={updateMemberSubscriptions}
            />
            <UpdateCustomerMemberInformationDialog
                open={editMemberInformationDialogOpen.open}
                member={editMemberInformationDialogOpen.member}
                setOpen={closeEditMemberInformationDialog}
                onSuccess={onMemberInfoUpdated}
                statuses={formData.statuses}
            />
            <AssignUserToCustomerDialog
                countries={formData?.countries ?? []}
                user={assignDialog.member}
                open={assignDialog.open}
                setOpen={(open) => setAssignDialog((prev) => ({...prev, open}))}
            />
            <ResendActivationDialog
                open={resendDialog.open}
                member={resendDialog.member}
                members={resendDialog.members}
                onClose={closeResendDialog}
            />
        </>
    );
}

export default CustomerMembersSection;


const EmptyContent = () => {

    return (
        <>
            <div className={'d-flex flex-column align-items-center'} style={{gap: 10}}>
                <NoUsersSvg
                    style={{
                        width: 'min(80%, 300px)',
                        height: 'auto',
                        maxHeight: 300,
                    }}
                />
                <Typography
                    variant={'body2'}
                    align={'center'}
                    color={'textSecondary'}
                >
                    There are no members for this customer. please add a member using the button above or ask them to
                    create an account in the client portal.
                </Typography>
            </div>
        </>
    );
}


const CustomerMembersSectionDataGrid = ({
                                            members,
                                            accessLevels,
                                            loading,
                                            removeMember,
                                            openMemberSubscriptions,
                                            openEditMemberInformationDialog,
                                            changeMemberAccessLevel,
                                            deleting,
                                            partner,
                                            statusActionOptions,
                                        }) => {
    const operatorAccessLevels = useAccessLevels();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.customers.members'});
    const dataGridSaveConfig = useMemo(() => ({
        name: 'CustomerProfileMembersListViewDataGrid',
        version: 5,
    }), [])

    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: UserGeneralInfoCompanyMembersDataGridCellKeys.actions,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinned: true,
                resizable: false,
                pinnedType: DataGridColumnPinnedTypes.left,
                width: 0,
            },
            {
                title: t('columns.status'),
                name: UserGeneralInfoCompanyMembersDataGridCellKeys.status,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 160,
            },
            {
                title: "Full Name",
                name: UserGeneralInfoCompanyMembersDataGridCellKeys.fullName,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 150,
            },
            {
                title: "Email",
                name: UserGeneralInfoCompanyMembersDataGridCellKeys.email,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 200,
            },
            {
                title: "Phone",
                name: UserGeneralInfoCompanyMembersDataGridCellKeys.phone,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            ...(operatorAccessLevels.packages.base.manageCustomerMemberAccessLevels ? [
                ...(accessLevels.map((e) => ({
                    title: e.partName,
                    name: e.partName,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: (e?.partName?.length ?? 11) * 9,
                    savable: false,
                }))),
                {
                    title: 'Admin',
                    name: UserGeneralInfoCompanyMembersDataGridCellKeys.admin,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: 100,
                },
            ] : []),
        ], [accessLevels, operatorAccessLevels.packages.base.manageCustomerMemberAccessLevels, t])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });


    /**
     * Memo version of table row records used in data grid
     */
    const rows = useMemo(() => {
        return members?.map(member => ({
            key: member.id,
            data: member,
            cells: {
                [UserGeneralInfoCompanyMembersDataGridCellKeys.actions]:
                    <ProfileMemberActionsMenu
                        member={member}
                        deleting={deleting}
                        showNotifications={operatorAccessLevels.packages.notifications.customerMembersNotificationChannels}
                        openMemberSubscriptions={openMemberSubscriptions}
                        openEditMemberInformationDialog={openEditMemberInformationDialog}
                        removeMember={removeMember}
                        statusActionOptions={statusActionOptions}
                        dataGrid
                    />,
                [UserGeneralInfoCompanyMembersDataGridCellKeys.status]:
                    <ProfileMemberStatusChip member={member}/>,
                [UserGeneralInfoCompanyMembersDataGridCellKeys.fullName]: `${member.firstName ?? ''} ${member.lastName ?? ''}`,
                [UserGeneralInfoCompanyMembersDataGridCellKeys.email]: member.email,
                [UserGeneralInfoCompanyMembersDataGridCellKeys.phone]: formatPhoneNumber(member.phone),
                ...(Object.fromEntries(accessLevels?.map((accessLevel) => ([
                    accessLevel.partName,
                    <div>
                        <Switch
                            color={'secondary'}
                            disabled={!!member.accessLevels?.find(e => e.id === accessLevel.id)?.loading || member.hasFullAccessLevel || deleting}
                            checked={!!member.accessLevels?.find(e => e.id === accessLevel.id) || member.hasFullAccessLevel}
                            onChange={(checked) => changeMemberAccessLevel(member, accessLevel, checked)}
                        />
                    </div>
                ])) ?? [])),
                [UserGeneralInfoCompanyMembersDataGridCellKeys.admin]:
                    <div>
                        <Switch
                            color={'primary'}
                            disabled={member.loadingAdmin || deleting}
                            checked={member.hasFullAccessLevel}
                            onChange={(value) => changeMemberAccessLevel(member, null, value)}
                        />
                    </div>,
            }
        })) ?? []
    }, [members, operatorAccessLevels.packages.notifications.customerMembersNotificationChannels, accessLevels, openMemberSubscriptions, removeMember, openEditMemberInformationDialog, changeMemberAccessLevel, deleting, statusActionOptions])

    return (
        <>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                {...!loading && {
                    EmptyContentComponent: <EmptyContent/>
                }}
                columns={dataGridColumns}
                rows={rows}
                classNames={{toolbar: 'pt-0'}}
                hideFooter
                visibleRows={8}
                hideExporting
                state={{
                    loading: {state: loading},
                    density: dataGridDensity,
                }}
            />
            {
                !loading && members.length === 0 && !!partner?.website &&
                <Button
                    component={'a'}
                    target={'_blank'}
                    href={partner?.website}
                    variant={'outlined'}
                    color={'primary'}
                    sx={{alignSelf: 'center'}}
                    disabled={deleting}
                >
                    Go to Client Portal
                </Button>
            }
        </>
    );
}

const CustomerMembersSectionCards = ({
                                         members,
                                         accessLevels,
                                         loading,
                                         removeMember,
                                         openMemberSubscriptions,
                                         openEditMemberInformationDialog,
                                         changeMemberAccessLevel,
                                         deleting,
                                         partner,
                                         statusActionOptions,
                                     }) => {
    const operatorAccessLevels = useAccessLevels();

    return (
        <>
            <Grid2 container spacing={2}>
                {
                    loading &&
                    Array.from({length: 2}).map((_, index) => (
                        <Grid2 xs={'auto'} key={index}>
                            <Skeleton height={294} sx={{transform: 'none', width: 'min(225px, 90vw)'}}/>
                        </Grid2>
                    ))
                }
                {
                    !loading &&
                    members.map((member) => (
                        <Grid2 key={member.id} xs={'auto'}>
                            <Card
                                variant={'outlined'}
                                sx={{
                                    paddingInlineStart: 2,
                                    paddingInlineEnd: 1,
                                    paddingBlock: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    width: 'min(250px, 100%)',
                                }}
                            >
                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    <Typography variant={'subtitle1'} fontWeight={600}>
                                        {`${member.firstName ?? ''} ${member.lastName ?? ''}`}
                                    </Typography>
                                    <ProfileMemberActionsMenu
                                        member={member}
                                        deleting={deleting}
                                        triggerDisabled={loading}
                                        showNotifications={operatorAccessLevels.packages.notifications.customerMembersNotificationChannels}
                                        openMemberSubscriptions={openMemberSubscriptions}
                                        openEditMemberInformationDialog={openEditMemberInformationDialog}
                                        removeMember={removeMember}
                                        statusActionOptions={statusActionOptions}
                                    />
                                </Box>
                                <Box sx={{alignSelf: 'flex-start', maxWidth: '100%'}}>
                                    <ProfileMemberStatusChip member={member}/>
                                </Box>
                                <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                                    <Typography variant={'body2'} color={'text.secondary'}>
                                        Email: {member.email}
                                    </Typography>
                                    <Typography variant={'body2'} color={'text.secondary'}>
                                        Phone: {formatPhoneNumber(member.phone)}
                                    </Typography>
                                </Box>
                                {
                                    operatorAccessLevels.packages.base.manageCustomerMemberAccessLevels &&
                                    <>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <Typography variant={'body2'}>
                                                Admin
                                            </Typography>
                                            <Switch
                                                color={'primary'}
                                                disabled={member.loadingAdmin || deleting}
                                                checked={member.hasFullAccessLevel}
                                                onChange={(value) => changeMemberAccessLevel(member, null, value)}
                                            />
                                        </Box>
                                        {
                                            accessLevels.map((accessLevel) => (
                                                <Box
                                                    key={accessLevel.id}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: 2
                                                    }}
                                                >
                                                    <Typography variant={'body2'}>
                                                        {accessLevel.partName}
                                                    </Typography>
                                                    <Switch
                                                        color={'secondary'}
                                                        disabled={!!member.accessLevels?.find(e => e.id === accessLevel.id)?.loading || member.hasFullAccessLevel || deleting}
                                                        checked={!!member.accessLevels?.find(e => e.id === accessLevel.id) || member.hasFullAccessLevel}
                                                        onChange={(checked) => changeMemberAccessLevel(member, accessLevel, checked)}
                                                    />
                                                </Box>
                                            ))
                                        }
                                    </>
                                }
                            </Card>
                        </Grid2>
                    ))
                }
            </Grid2>

            {
                !loading && members.length === 0 && !!partner?.website &&
                <>
                    <EmptyContent/>
                    <Button
                        component={'a'}
                        target={'_blank'}
                        href={partner?.website}
                        variant={'outlined'}
                        color={'primary'}
                        sx={{alignSelf: 'center'}}
                        disabled={deleting}
                    >
                        Go to Client Portal
                    </Button>
                </>
            }
        </>
    );
}

